import { useEffect, useState } from "react";

/** A tiny path router on window.history: the current path in state, `navigate()` to move, `<a href>` intercepted in App. */

export type Route =
  | { name: "home" }
  | { name: "signin"; magicToken?: string }
  | { name: "trip"; id: string }
  | { name: "proposal"; id: string }
  | { name: "account" }
  | { name: "club" }
  | { name: "conversation" }
  | { name: "residents" }
  | { name: "link"; token: string }
  | { name: "staff" }
  | { name: "privacy" }
  | { name: "deleteConfirm"; token: string }
  | { name: "notFound"; path: string };

export function matchRoute(path: string): Route {
  const clean = path.replace(/\/+$/, "") || "/";
  const parts = clean.split("/").filter(Boolean);
  const [a, b, c] = parts;
  if (parts.length === 0) return { name: "home" };
  if (a === "signin" && parts.length === 1) return { name: "signin" };
  if (a === "signin" && b && parts.length === 2) return { name: "signin", magicToken: decodeURIComponent(b) };
  if (a === "trips" && b && parts.length === 2) return { name: "trip", id: decodeURIComponent(b) };
  if (a === "p" && b && parts.length === 2) return { name: "proposal", id: decodeURIComponent(b) };
  if (a === "account" && parts.length === 1) return { name: "account" };
  if ((a === "club" || a === "circle") && parts.length === 1) return { name: "club" }; // `/circle` is the old name of the programme page, kept for links already sent
  if (a === "conversation" && parts.length === 1) return { name: "conversation" };
  if (a === "residents" && parts.length === 1) return { name: "residents" };
  if (a === "t" && b && parts.length === 2) return { name: "link", token: decodeURIComponent(b) };
  if (a === "staff" && parts.length === 1) return { name: "staff" };
  if (a === "privacy" && parts.length === 1) return { name: "privacy" };
  if (a === "account" && b === "delete" && c && parts.length === 3) return { name: "deleteConfirm", token: decodeURIComponent(c) };
  return { name: "notFound", path: clean };
}

const listeners = new Set<() => void>();

export function currentPath(): string {
  return window.location.pathname;
}

export function navigate(path: string, options: { replace?: boolean } = {}) {
  if (path === currentPath()) return;
  if (options.replace) window.history.replaceState(null, "", path);
  else window.history.pushState(null, "", path);
  window.scrollTo({ top: 0 });
  for (const l of listeners) l();
}

export function usePath(): string {
  const [path, setPath] = useState(currentPath);
  useEffect(() => {
    const update = () => setPath(currentPath());
    listeners.add(update);
    window.addEventListener("popstate", update);
    return () => {
      listeners.delete(update);
      window.removeEventListener("popstate", update);
    };
  }, []);
  return path;
}

/** Internal links: same-origin, left click, no modifier, no download or target. */
export function interceptLink(event: MouseEvent) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target instanceof Element ? event.target.closest("a") : null;
  if (!target || target.target || target.hasAttribute("download") || target.getAttribute("rel") === "external") return;
  const href = target.getAttribute("href");
  if (!href || !href.startsWith("/") || href.startsWith("//")) return;
  event.preventDefault();
  navigate(href);
}
